import { Stack, TextField } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CrudPage } from '../../shared/components/CrudPage';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { resources } from '../../core/services/resources';
import { formatDate } from '../../shared/utils/format';
import type { AIModel, GeoLocation, VisibilityWeight } from '../../core/types/data-models';

/** ISO datetime → `YYYY-MM-DD` for the native date input. */
const dateToInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '');
/** Native date input value → ISO datetime (midnight UTC) or null when cleared. */
const inputToDate = (v: string) => (v ? new Date(`${v}T00:00:00.000Z`).toISOString() : null);

export function VisibilityWeightsPage() {
  const geosQuery = useQuery({
    queryKey: ['geo-locations', 'select'],
    queryFn: () => resources.geoLocations.list({ pageSize: 500, sort: 'name' }),
  });
  const modelsQuery = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 500, sort: 'name' }),
  });

  const geoNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of geosQuery.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [geosQuery.data]);
  const modelNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of modelsQuery.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [modelsQuery.data]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'geoLocationId',
        headerName: 'Geo location',
        flex: 1,
        minWidth: 180,
        renderCell: (p: GridRenderCellParams) => {
          const id = p.value as number | null | undefined;
          return id == null ? 'Any' : geoNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'aiModelId',
        headerName: 'AI model',
        flex: 1,
        minWidth: 180,
        renderCell: (p: GridRenderCellParams) => {
          const id = p.value as number | null | undefined;
          return id == null ? 'Any' : modelNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'weight',
        headerName: 'Weight',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      { field: 'startPeriod', headerName: 'Start', width: 160, valueFormatter: (v) => (v ? formatDate(v as string) : '') },
      { field: 'endPeriod', headerName: 'End', width: 160, valueFormatter: (v) => (v ? formatDate(v as string) : '') },
    ],
    [geoNameById, modelNameById],
  );

  return (
    <CrudPage<VisibilityWeight>
      title="Visibility weights"
      entityName="Visibility weight"
      description="Predefined visibility weight per geo location × AI model combination, optionally bounded to a validity period. Leave geo or model empty to not pin the weight to that dimension."
      cacheKey={['visibility-weights']}
      service={resources.visibilityWeights}
      columns={columns}
      defaultValues={{ geoLocationId: null, aiModelId: null, weight: null, startPeriod: null, endPeriod: null }}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          <EntitySelect<GeoLocation>
            cacheKey={['geo-locations', 'select']}
            service={resources.geoLocations}
            label="Geo location"
            value={values.geoLocationId ?? null}
            onChange={(id) => setValues({ ...values, geoLocationId: id })}
            helperText="Empty = any geo location."
          />
          <EntitySelect<AIModel>
            cacheKey={['ai-models', 'select']}
            service={resources.aiModels}
            label="AI model"
            value={values.aiModelId ?? null}
            onChange={(id) => setValues({ ...values, aiModelId: id })}
            helperText="Empty = any AI model."
          />
          <TextField
            label="Weight"
            type="number"
            inputProps={{ step: 0.01 }}
            value={values.weight ?? ''}
            onChange={(e) =>
              setValues({ ...values, weight: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
          <TextField
            label="Start period"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={dateToInput(values.startPeriod)}
            onChange={(e) => setValues({ ...values, startPeriod: inputToDate(e.target.value) })}
          />
          <TextField
            label="End period"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={dateToInput(values.endPeriod)}
            onChange={(e) => setValues({ ...values, endPeriod: inputToDate(e.target.value) })}
          />
        </Stack>
      )}
    />
  );
}
